import {
  ORDER_STATUS_LABELS, PAYMENT_STATUS_LABELS, REORDER_CONFIDENCE_LABELS, SAMPLE_STATUS_LABELS, formatDate, formatILS, formatRelativeDay,
} from '@takumi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Package, Plus, RefreshCcw, ShoppingBag, Tag, TrendingUp } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field, Input, Select } from '@/components/ui/form';
import { Badge, Card, CardHeader, Dialog, DialogContent, EmptyState, Skeleton } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useInvalidateCrm } from '@/lib/queries';
import type { CustomerPrice, OrgSales } from '@/lib/sales-types';
import { cn } from '@/lib/utils';
import { ORDER_STATUS_VARIANT, PAYMENT_STATUS_VARIANT } from '@/pages/OrderPage';
import { QuotesCard } from '@/pages/QuotePage';
import { useProducts } from './DocumentEditor';
import { SampleStatusDialog } from './PaymentDialogs';

export function useOrgSales(orgId: string) {
  return useQuery({ queryKey: ['orgSales', orgId], queryFn: () => api.get<OrgSales>(`/organizations/${orgId}/sales`) });
}

export function OrgSalesPanel({ orgId }: { orgId: string }) {
  const { data, isLoading } = useOrgSales(orgId);
  if (isLoading || !data) return <div className="space-y-3 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12" />)}</div>;
  const s = data.stats;
  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Kpi label="סה״כ הכנסות" value={formatILS(s.revenue, { decimals: false })} />
        <Kpi label="הזמנות" value={String(s.orderCount)} />
        <Kpi label="הזמנה ממוצעת" value={formatILS(s.averageOrder, { decimals: false })} />
        <Kpi label="הזמנה אחרונה" value={s.lastOrderDate ? formatDate(s.lastOrderDate) : '—'} />
        <Kpi label="יתרה פתוחה" value={formatILS(s.outstanding)} tone={s.outstanding > 0 ? 'overdue' : undefined} />
      </div>
      <ReorderCard orgId={orgId} data={data} />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="הזמנות" icon={<ShoppingBag />} count={data.orders.length} />
          {data.orders.length ? (
            <ul className="divide-y divide-line/70">
              {data.orders.map((o) => (
                <li key={o.id}>
                  <Link to={`/orders/${o.id}`} className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-sm hover:bg-sunken/60">
                    <span className="ltr font-semibold">{o.number}</span>
                    <span className="text-muted">{formatDate(o.orderDate)}</span>
                    <Badge variant={ORDER_STATUS_VARIANT[o.status]}>{ORDER_STATUS_LABELS[o.status]}</Badge>
                    <Badge variant={PAYMENT_STATUS_VARIANT[o.paymentStatus]}>{PAYMENT_STATUS_LABELS[o.paymentStatus]}</Badge>
                    <span className="ms-auto font-semibold tabular">{formatILS(o.total)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : <EmptyState title="אין הזמנות עדיין" />}
        </Card>
        <QuotesCard quotes={data.quotes} orgId={orgId} />
        <SamplesCard orgId={orgId} samples={data.samples} />
        <Card>
          <CardHeader title="מוצרים מובילים" icon={<TrendingUp />} />
          {data.topProducts.length ? (
            <ul className="divide-y divide-line/70 text-sm">
              {data.topProducts.map((p) => (
                <li key={p.description} className="flex items-center gap-2 px-4 py-2.5">
                  <span className="min-w-0 flex-1 truncate font-semibold">{p.description}</span>
                  <span className="text-muted">{Number(p.quantity)} יח׳ · {p.orders} הזמנות</span>
                  <span className="font-semibold tabular">{formatILS(Number(p.revenue), { decimals: false })}</span>
                </li>
              ))}
            </ul>
          ) : <EmptyState title="אין נתוני מכירות" />}
        </Card>
      </div>
      <CustomerPricesCard orgId={orgId} />
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: 'overdue' }) {
  return (
    <div className="rounded-card border border-line bg-surface px-3 py-2.5">
      <div className="text-xs font-semibold text-muted">{label}</div>
      <div className={cn('mt-0.5 font-display text-lg font-semibold tabular', tone === 'overdue' && 'text-overdue')}>{value}</div>
    </div>
  );
}

function ReorderCard({ orgId, data }: { orgId: string; data: OrgSales }) {
  const r = data.reorder;
  const invalidate = useInvalidateCrm();
  const followup = useMutation({
    mutationFn: () => api.post(`/my-day/sales/reorders/${orgId}/followup`, {}),
    onSuccess: () => {
      invalidate(orgId);
      toast.success('נקבע פולואפ לבדיקת חידוש מלאי');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (r.status === 'insufficient_data') {
    return (
      <Card className="flex items-center gap-3 p-4 text-sm text-muted">
        <RefreshCcw className="size-5" /> הערכת הזמנה חוזרת: {r.explanation}
      </Card>
    );
  }
  const tone = r.status === 'overdue' ? 'border-overdue/30 bg-overdue-bg/50' : r.status === 'due_soon' ? 'border-today/30 bg-today-bg/50' : '';
  return (
    <Card className={cn('flex flex-wrap items-center gap-4 p-4', tone)}>
      <RefreshCcw className={cn('size-6', r.status === 'overdue' ? 'text-overdue' : r.status === 'due_soon' ? 'text-today' : 'text-matcha-600')} />
      <div className="min-w-0 flex-1">
        <div className="font-semibold">
          {r.status === 'overdue' ? 'ייתכן שהלקוח צריך חידוש מלאי' : r.status === 'due_soon' ? 'הזמנה חוזרת צפויה בקרוב' : 'הזמנה חוזרת צפויה'}
          {r.expectedNextDay && <span className="font-normal text-muted"> · צפי: {formatDate(r.expectedNextDay)} ({formatRelativeDay(r.expectedNextDay + 'T09:00:00Z', new Date(), false)})</span>}
        </div>
        <div className="text-sm text-ink-soft">{r.explanation}</div>
        <div className="mt-0.5 text-xs text-muted">
          רמת ביטחון: {REORDER_CONFIDENCE_LABELS[r.confidence]} · מרווחים: {r.intervals.join(', ')} ימים · סבולת {r.toleranceDays} ימים
        </div>
      </div>
      {r.status !== 'ok' && (
        <Button variant="secondary" onClick={() => followup.mutate()} loading={followup.isPending}>
          <CalendarClock /> יצירת פולואפ
        </Button>
      )}
    </Card>
  );
}

function SamplesCard({ orgId, samples }: { orgId: string; samples: OrgSales['samples'] }) {
  const [editing, setEditing] = useState<string | null>(null);
  return (
    <Card>
      <CardHeader title="דוגמיות" icon={<Package />} count={samples.length} />
      {samples.length ? (
        <ul className="divide-y divide-line/70 text-sm">
          {samples.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-2 px-4 py-2.5">
              <span className="font-semibold">{s.productName ?? 'דוגמית'}</span>
              <Badge variant={s.status === 'converted' ? 'matcha' : s.status === 'not_relevant' ? 'neutral' : 'tomorrow'}>{SAMPLE_STATUS_LABELS[s.status]}</Badge>
              <span className="text-muted">{s.sentAt ? `נשלחה ${formatDate(s.sentAt)}` : `נוצרה ${formatDate(s.createdAt)}`}</span>
              {s.feedback && <span className="w-full text-xs text-ink-soft">משוב: {s.feedback}</span>}
              {!['converted', 'not_relevant', 'feedback_received'].includes(s.status) && (
                <Button size="sm" variant="ghost" className="ms-auto" onClick={() => setEditing(s.id)}>עדכון</Button>
              )}
            </li>
          ))}
        </ul>
      ) : <EmptyState title="לא נשלחו דוגמיות" />}
      {editing && <SampleStatusDialog sampleId={editing} organizationId={orgId} open onOpenChange={(o) => !o && setEditing(null)} />}
    </Card>
  );
}

function CustomerPricesCard({ orgId }: { orgId: string }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const prices = useQuery({ queryKey: ['customerPrices', orgId], queryFn: () => api.get<CustomerPrice[]>(`/pricing/customers/${orgId}`) });
  const end = useMutation({
    mutationFn: (id: string) => api.post(`/pricing/customers/${orgId}/${id}/end`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['customerPrices', orgId] }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const today = new Date().toISOString().slice(0, 10);
  const active = prices.data?.filter((p) => !p.validTo || p.validTo >= today) ?? [];
  return (
    <Card>
      <CardHeader
        title="מחירים מוסכמים"
        icon={<Tag />}
        count={active.length}
        actions={can('pricing.manage') && <Button size="sm" variant="ghost" onClick={() => setAdding(true)}><Plus /> מחיר ללקוח</Button>}
      />
      {active.length ? (
        <ul className="divide-y divide-line/70 text-sm">
          {active.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-2 px-4 py-2.5">
              <span className="font-semibold">{p.productName}</span>
              {p.isContract && <Badge variant="gold">חוזה</Badge>}
              <span className="text-muted">מ-{Number(p.minQty)} יח׳</span>
              <span className="font-semibold tabular">{p.unitPrice != null ? formatILS(p.unitPrice) : `${p.discountPct}% הנחה`}</span>
              <span className="text-xs text-muted">(מחירון {formatILS(p.listPrice)}){p.validTo && ` · עד ${formatDate(p.validTo)}`}</span>
              {can('pricing.manage') && (
                <Button size="sm" variant="ghost" className="ms-auto" onClick={() => confirm('לסיים את תוקף המחיר?') && end.mutate(p.id)}>סיום תוקף</Button>
              )}
            </li>
          ))}
        </ul>
      ) : <EmptyState title="אין מחירים מיוחדים" description="בהצעות ובהזמנות ייעשה שימוש במחירון, במדרגות כמות ובהנחת הלקוח" />}
      <CustomerPriceDialog orgId={orgId} open={adding} onOpenChange={setAdding} />
    </Card>
  );
}

function CustomerPriceDialog({ orgId, open, onOpenChange }: { orgId: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const products = useProducts();
  const qc = useQueryClient();
  const [productId, setProductId] = useState('');
  const [mode, setMode] = useState<'price' | 'discount'>('price');
  const [value, setValue] = useState('');
  const [minQty, setMinQty] = useState('1');
  const [isContract, setIsContract] = useState(false);
  const [validTo, setValidTo] = useState('');
  const save = useMutation({
    mutationFn: () => api.post(`/pricing/customers/${orgId}`, {
      productId, minQty: Number(minQty) || 1, isContract, validTo: validTo || null,
      ...(mode === 'price' ? { unitPrice: Number(value) } : { discountPct: Number(value) }),
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customerPrices', orgId] });
      toast.success('המחיר נשמר ויחול אוטומטית בהצעות ובהזמנות');
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!productId || value === '') return toast.error('יש לבחור מוצר ולהזין ערך');
    save.mutate();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="מחיר מוסכם ללקוח">
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <Field label="מוצר" className="sm:col-span-2">
            {(id) => (
              <Select id={id} value={productId} onChange={(e) => setProductId(e.target.value)}>
                <option value="">בחרו…</option>
                {products.data?.map((p) => <option key={p.id} value={p.id}>{p.name} — מחירון {formatILS(p.listPrice)}</option>)}
              </Select>
            )}
          </Field>
          <Field label="סוג">
            {(id) => (
              <Select id={id} value={mode} onChange={(e) => setMode(e.target.value as 'price' | 'discount')}>
                <option value="price">מחיר קבוע ליחידה</option>
                <option value="discount">אחוז הנחה ממחירון</option>
              </Select>
            )}
          </Field>
          <Field label={mode === 'price' ? 'מחיר (₪, לפני מע״מ)' : 'הנחה %'}>{(id) => <Input id={id} type="number" min={0} step="0.01" value={value} onChange={(e) => setValue(e.target.value)} />}</Field>
          <Field label="מכמות">{(id) => <Input id={id} type="number" min={0.001} step="0.001" value={minQty} onChange={(e) => setMinQty(e.target.value)} />}</Field>
          <Field label="בתוקף עד (אופציונלי)">{(id) => <Input id={id} type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />}</Field>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" className="size-4 accent-matcha-700" checked={isContract} onChange={(e) => setIsContract(e.target.checked)} /> מחיר חוזה (גובר על כל מחיר אחר)
          </label>
          <div className="flex justify-end gap-2 border-t border-line pt-4 sm:col-span-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>ביטול</Button>
            <Button type="submit" loading={save.isPending}>שמירה</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
